import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import {
  addDisplayName,
  addStateHandlers,
  addWrapper,
  addWrapperHOC,
  branch,
  flowMax,
  returns,
} from 'ad-hok'
import { branchIfTruthy } from 'ad-hok-utils'
import React, { FC } from 'react'
import { CookiesProvider } from 'react-cookie'
import ReactDOM from 'react-dom'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import {
  addAdminOrCreatorOrRedirect,
  addConfigContextProvider,
  addUserContextProvider,
} from 'shared/src/api'
import { LogOut } from 'shared/src/components/LogOut'
import { addMaintenancePage } from 'shared/src/components/Maintenance'
import 'shared/src/translations/setup'
import { addLocation, addNavigate } from 'shared/src/utils/addNavigate'
import { AuthProvider, addAuthContext } from 'shared/src/utils/auth'
import { getEnvVar } from 'shared/src/utils/env'
import { LoadingProvider } from 'shared/src/utils/loading'

import maintenanceImage from 'assets/maintenance.png'
import { CatchAll } from 'components/CatchAll'
import { UpdatePassword } from 'components/auth/UpdatePassword'
import { EarningsDay } from 'components/creatorEarnings/EarningsDay'
import { EarningsOverview } from 'components/creatorEarnings/EarningsOverview'
import { EarningsUser } from 'components/creatorEarnings/EarningsUser'
import { CreatorFund } from 'components/creatorFund'
import { RedemptionRequest } from 'components/creatorFund/RedemptionRequest'
import { Layout } from './components/Layout'
import { Messaging } from './components/Messaging'
import { Misc } from './components/Misc'
import { Page } from './components/Page'
import { ResourceEditPage } from './components/ResourceEdit'
import { ResourceIndex } from './components/ResourceIndex'
import { Account } from './components/account/index'
import { Onboarding } from './components/auth'
import { Login } from './components/auth/Login'
import { Register } from './components/auth/Register'
import { Edits } from './components/edits'
import { Insights } from './components/insights'
import { MediaQueue } from './components/mediaQueue/index'
import { MyRodeo } from './components/myRodeo'
import { Post } from './components/post'
import { addProductLinkingContextProvider } from './components/post/productLinking'
import { Product } from './components/product'
import { addToastContextProvider } from './components/toastContext'
import { UserEditForm } from './components/userEdits'
import { Welcome } from './components/welcome'
import './index.css'
import reportWebVitals from './reportWebVitals'
import { addCameFromOnboardingContextProvider } from './utils/cameFromOnboardingContext'
import { addLaunchImageShowing } from './utils/launchImage'
import {
  clearShouldResumeOnboardingAfterOauthRedirection,
  getShouldResumeOnboardingAfterOauthRedirection,
} from './utils/localStorage'

const SENTRY_URL = getEnvVar('SENTRY_URL')

Sentry.init({
  dsn: SENTRY_URL,
  integrations: [new BrowserTracing()],
  tracesSampleRate: 1.0,
  environment: process.env.NODE_ENV,
})

const AuthRouter: FC = () => (
  <Routes>
    <Route index element={<Welcome />} />
    <Route path="register" element={<Register />} />
    <Route path="login" element={<Login />} />
    <Route path="resetPassword" element={<UpdatePassword />} />
    <Route path="*" element={<CatchAll />} />
  </Routes>
)

const AdminRouter: FC = () => (
  <Routes>
    <Route index element={<Page />} />
    <Route path="users/:userId/feed" element={<MyRodeo />} />
    <Route path="users/:userId/posts">
      <Route index element={<MediaQueue />} />
      <Route path="create" element={<Post />} />
      <Route path=":resourceId" element={<Post />} />
      <Route path="products">
        <Route path="create" element={<Product />} />
      </Route>
    </Route>
    <Route path="users/:userId/userLists">
      <Route index element={<Edits />} />
      <Route path=":resourceId" element={<UserEditForm />} />
      <Route
        path=":resourceId/edit"
        element={<UserEditForm editMode={true} />}
      />
      <Route path="products">
        <Route path="create" element={<Product />} />
      </Route>
    </Route>
    <Route path="users/:userId/insights" element={<Insights />} />
    <Route path=":resourceName">
      <Route index element={<ResourceIndex />} />
      <Route path="create" element={<ResourceEditPage />} />
      <Route path=":resourceId" element={<ResourceEditPage />} />
    </Route>
    <Route path="users/:userId/earnings" element={<CreatorFund />} />
    <Route path="creatorEarnings">
      <Route index element={<EarningsOverview />} />
      <Route path=":date" element={<EarningsDay />} />
      <Route path="user/:userId" element={<EarningsUser />} />
      <Route path="user/:userId/redeem" element={<RedemptionRequest />} />
      <Route
        path="user/:userId/redeem/:redemptionId"
        element={<RedemptionRequest />}
      />
    </Route>
    <Route path="misc" element={<Misc />} />
    <Route path="messages" element={<Messaging />} />
    <Route path="*" element={<CatchAll />} />
  </Routes>
)

const UserRouter: FC = () => (
  <Routes>
    <Route index element={<MyRodeo />} />
    <Route path="posts">
      <Route index element={<MediaQueue />} />
      <Route path="create" element={<Post />} />
      <Route path=":resourceId" element={<Post />} />
      <Route path="products">
        <Route path="create" element={<Product />} />
      </Route>
    </Route>
    <Route path="userLists">
      <Route index element={<Edits />} />
      <Route path=":resourceId" element={<UserEditForm />} />
      <Route
        path=":resourceId/edit"
        element={<UserEditForm editMode={true} />}
      />
      <Route path="products">
        <Route path="create" element={<Product />} />
      </Route>
    </Route>
    <Route path="account">
      <Route index element={<Account />} />
      <Route path="linkInstagram" element={<Account firstStep="instagram" />} />
    </Route>
    <Route path="insights">
      <Route index element={<Insights />} />
    </Route>
    <Route path="earnings">
      <Route index element={<CreatorFund />} />
      <Route path="redeem" element={<RedemptionRequest />} />
      <Route path="redeem/:redemptionId" element={<RedemptionRequest />} />
    </Route>
    <Route path="*" element={<CatchAll />} />
  </Routes>
)

const App: FC = flowMax(
  addDisplayName('CMS'),
  addWrapperHOC((component) => Sentry.withErrorBoundary(component, {})),
  addWrapper((render) => (
    <HelmetProvider>
      <BrowserRouter>
        <CookiesProvider>
          <AuthProvider>
            <LoadingProvider>{render()}</LoadingProvider>
          </AuthProvider>
        </CookiesProvider>
      </BrowserRouter>
    </HelmetProvider>
  )),
  addMaintenancePage(maintenanceImage),
  addToastContextProvider,
  // if path is /logout, log the user out
  addLocation,
  branch(
    ({ location }) => location.pathname === '/logout',
    returns(() => <LogOut />)
  ),
  addAuthContext,
  // show welcome if not logged in'
  branch(
    ({ isAuthenticated }) => !isAuthenticated,
    returns(() => (
      <Layout mode="creator">
        <AuthRouter />
      </Layout>
    ))
  ),
  // redirect if not an admin or creator
  addUserContextProvider,
  addConfigContextProvider,
  addAdminOrCreatorOrRedirect,
  addCameFromOnboardingContextProvider,
  addProductLinkingContextProvider,
  branch(
    ({ user }) => user.isAdmin,
    returns(() => (
      <Layout mode="admin">
        <AdminRouter />
      </Layout>
    ))
  ),
  addNavigate,
  // show onboarding if not begun or in progress and user is creator
  addStateHandlers(
    ({ user }) => ({
      isOnboarding:
        user.isCreator &&
        (!user.onboardingStartedAt ||
          getShouldResumeOnboardingAfterOauthRedirection()),
    }),
    {
      finishOnboarding:
        (_, { setCameFromOnboarding, navigate }) =>
        () => {
          clearShouldResumeOnboardingAfterOauthRedirection()
          setCameFromOnboarding(true)
          navigate('/posts')
          return {
            isOnboarding: false,
          }
        },
    }
  ),
  branchIfTruthy('isOnboarding', {
    returns: ({ finishOnboarding }) => (
      <Onboarding onFinish={finishOnboarding} />
    ),
  }),
  addLaunchImageShowing,
  // render normal routes
  () => (
    <Layout mode="creator">
      <UserRouter />
    </Layout>
  )
)

ReactDOM.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
  document.getElementById('root')
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
